import { router } from 'expo-router'
import { useState } from 'react'
import { Platform } from 'react-native'
import { useUnistyles } from 'react-native-unistyles'

import useDeleteAction from '@/components/fosters/actions/useDeleteAction'
import useMarkDeceased from '@/components/fosters/actions/useMarkDeceased'
import usePrintPdfAction from '@/components/fosters/actions/usePrintPdfAction'
import useFile from '@/hooks/useFile'
import useFosterOwner from '@/hooks/useFosterOwner'
import FosterService from '@/services/fosterService'
import { useAppStore } from '@/store/useAppStore'
import buildOrgPath from '@/utils/orgRoute'
import trpc from '@/utils/trpc'

export interface FosterAction {
  id: string
  icon: string
  label: string
  testID: string
  onPress: () => void | Promise<void>
  visible: boolean
  color?: string
  disabled?: boolean
}

export const useFosterActions = (fosterId: string) => {
  const { theme } = useUnistyles()
  const { foster } = FosterService.useFoster(fosterId)
  const currentOrganization = useAppStore.use.currentOrganization().organization
  const fosterOwner = useFosterOwner(fosterId)

  const pdfAction = usePrintPdfAction(fosterId, currentOrganization?.id || '')
  const { deleteFoster } = useDeleteAction({ fosterId })
  const { markDeceased } = useMarkDeceased(fosterId)
  const copyFosterMutation = trpc.foster.copy.useMutation()
  const { openFile, scanFiles } = useFile()

  const [newAdoptionRecordOpen, setNewAdoptionRecordOpen] = useState(false)
  const [returnAdoptionOpen, setReturnAdoptionOpen] = useState(false)

  const isSubscriptionActive = currentOrganization?.subscriptionActive === true

  const handleCopyFoster = async () => {
    await copyFosterMutation.mutateAsync(
      {
        id: fosterId,
        organizationId: currentOrganization?.id || '',
      },
      {
        onSuccess: (newFoster) => {
          router.navigate({
            pathname: buildOrgPath('/fosters/[id]'),
            params: { id: newFoster.id },
          })
        },
      }
    )
  }

  const handleScanDocuments = async () => {
    const uris = await scanFiles()
    const scanUrisParam =
      uris && uris.length > 0 ? `?scanUris=${uris.join(',')}` : ''
    router.push(buildOrgPath(`/fosters/${fosterId}/documents${scanUrisParam}`))
  }

  const handlePrintAdoptionContract = async () => {
    await openFile({
      fileId: currentOrganization?.adoptionContract?.id,
      fileName: currentOrganization?.adoptionContract?.name,
      mimeType: currentOrganization?.adoptionContract?.mimeType,
      fosterName: foster?.name,
      contextName: 'adoption_contract',
    })
  }

  const handlePrintCoverSheet = async () => {
    await pdfAction.openPDF()
  }

  const handleNewAdoptionRecord = () => {
    setNewAdoptionRecordOpen(true)
  }

  const handleReturnAdoption = () => {
    setReturnAdoptionOpen(true)
  }

  const actions: FosterAction[] = [
    // Delete - only for foster owners (FAB: first action)
    {
      id: 'delete',
      icon: 'delete',
      label: 'Delete',
      testID: 'delete-button',
      onPress: deleteFoster,
      visible: fosterOwner && isSubscriptionActive,
      color: theme.colors.error,
    },
    // Mark Deceased - only for foster owners when foster is not already deceased (FAB: second action)
    {
      id: 'mark-deceased',
      icon: 'grave-stone',
      label: 'Mark Deceased',
      testID: 'mark-deceased-button',
      onPress: markDeceased,
      visible: fosterOwner && !foster?.deceasedDate && isSubscriptionActive,
    },
    // Copy Foster - only for foster owners (FAB: third action)
    {
      id: 'copy-foster',
      icon: 'content-copy',
      label: 'Copy Foster',
      testID: 'copy-foster-button',
      onPress: handleCopyFoster,
      visible: fosterOwner && isSubscriptionActive,
    },
    // Print Adoption Contract - only when organization has adoption contract (FAB: fourth action)
    {
      id: 'print-adoption-contract',
      icon: 'clipboard-flow',
      label: 'Print Adoption Contract',
      testID: 'print-adoption-contract-button',
      onPress: handlePrintAdoptionContract,
      visible:
        !!currentOrganization?.adoptionContract?.id && isSubscriptionActive,
    },
    // Print Cover Sheet - always visible when subscription is active (FAB: fifth action)
    {
      id: 'print-cover-sheet',
      icon: 'newspaper-variant',
      label: 'Print Cover Sheet',
      testID: 'print-cover-sheet-button',
      onPress: handlePrintCoverSheet,
      visible: isSubscriptionActive,
    },
    // New Adoption Record / Mark Returned - only for foster owners (FAB: sixth action)
    {
      id: 'adoption-action',
      icon: foster?.isAdopted ? 'clipboard-minus' : 'clipboard-plus',
      label: foster?.isAdopted ? 'Mark Returned' : 'New Adoption Record',
      testID: foster?.isAdopted
        ? 'adoption-return-button'
        : 'new-adoption-record-button',
      onPress: foster?.isAdopted
        ? handleReturnAdoption
        : handleNewAdoptionRecord,
      visible: fosterOwner && isSubscriptionActive,
    },
    // Scan Documents - only for foster owners on native platforms (FAB: seventh action)
    {
      id: 'scan-documents',
      icon: 'scanner',
      label: 'Scan Documents',
      testID: 'scan-documents-button',
      onPress: handleScanDocuments,
      visible: fosterOwner && Platform.OS !== 'web' && isSubscriptionActive,
    },
  ]

  return {
    actions: actions.filter((action) => action.visible),
    allActions: actions,
    isSubscriptionActive,
    fosterOwner,
    foster,
    currentOrganization,
    // Modal states for adoption actions
    newAdoptionRecordOpen,
    setNewAdoptionRecordOpen,
    returnAdoptionOpen,
    setReturnAdoptionOpen,
    // Individual handlers for cases where components need direct access
    handlers: {
      handleCopyFoster,
      handleScanDocuments,
      handlePrintAdoptionContract,
      handlePrintCoverSheet,
      handleNewAdoptionRecord,
      handleReturnAdoption,
      deleteFoster,
      markDeceased,
    },
  }
}
